/**
 * @file: OwnMessageStatus.tsx
 *
 * The status column shown to the LEFT of an own message bubble: a spinner
 * while sending, a tappable retry on failure, or the double-blue check
 * (tap to see who read it) once read. Renders nothing otherwise — a plain
 * sent message carries no indicator.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AlertCircle, CheckCheck } from 'lucide-react-native';
import { FC, memo } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export type OwnMessageStatusKind = 'sending' | 'failed' | 'read' | null;

const kColumnStyle: ViewStyle = {
  alignSelf: 'flex-end',
  justifyContent: 'center',
};

export const OwnMessageStatus: FC<{
  kind: OwnMessageStatusKind;
  onRetry?: () => void;
  onShowReaders?: () => void;
  style?: StyleProp<ViewStyle>;
}> = memo(({ kind, onRetry, onShowReaders, style }) => {
  const { rpx } = useResponsiveContext();
  const column = [kColumnStyle, style];

  if (kind === 'sending') {
    return (
      <View style={column}>
        <ActivityIndicator size="small" color="#9ca3af" />
      </View>
    );
  }
  if (kind === 'failed') {
    return (
      <TouchableOpacity onPress={onRetry} hitSlop={kHitSlop} style={column}>
        <AlertCircle color="#ef4444" size={rpx(18)} />
      </TouchableOpacity>
    );
  }
  if (kind === 'read') {
    return (
      <TouchableOpacity
        onPress={onShowReaders}
        hitSlop={kHitSlop}
        style={column}
      >
        <CheckCheck color="#3b82f6" size={rpx(16)} />
      </TouchableOpacity>
    );
  }
  return null;
});
